import format from 'date-fns/format';
import JSCookie from 'js-cookie';
import { OptionalArraySchema } from 'yup/lib/array';
import { AnyObject } from 'yup/lib/types';

import Checkbox, { CheckboxProps } from '@tmh-website/ui/Checkbox';
import DatePicker, { DatePickerProps } from '@tmh-website/ui/DatePicker';
import FileUploader, { FileUploaderProps } from '@tmh-website/ui/FileUploader';
import Input, { InputProps } from '@tmh-website/ui/Input';
import Multiselect, { MultiselectProps } from '@tmh-website/ui/Multiselect';
import NumberInput, { NumberInputProps } from '@tmh-website/ui/NumberInput';
import RadioGroup, { RadioGroupProps } from '@tmh-website/ui/RadioGroup';
import Select, { SelectProps } from '@tmh-website/ui/Select';
import Textarea, { TextareaProps } from '@tmh-website/ui/Textarea';
import Yup from '@tmh-website/services/validation.service';
import { cmsWebClient } from '@tmh-website/services/cms.client.service';
import { FormElementThemeColor } from '@tmh-website/ui/FormElements';
import { HubspotRegion } from '@tmh-website/services/form.service';
import { UploadFile } from '@tmh-website/services/upload.service';

import {
  CanHaveHubspotRegion,
  FieldType,
  HasHubspotFormId,
  HubSpotDependentCondition,
  HubSpotField,
  HubSpotFormEntity,
  HubSpotLegalConsentOptionsType,
  HubSpotOption,
  HubSpotSubmitContext,
  HubSpotSubmitObject,
} from './HubSpotFormRenderer.types';
import { HubSpotOperatorToValidationFnMap } from './hubspot-form-renderer.operators.service';

export const DEFAULT_HUBSPOT_REGION: HubspotRegion = HubspotRegion.Europe;

type FormComponentMapEntry<T> = {
  [key in keyof T]: (props: T[key]) => JSX.Element;
};

type FormComponentPropsMap = {
  dropdown: SelectProps;
  radio: RadioGroupProps;
  number: NumberInputProps;
  single_checkbox: CheckboxProps;
  multiple_checkboxes: MultiselectProps;
  multi_line_text: TextareaProps;
  single_line_text: InputProps;
  file: FileUploaderProps;
  phone: InputProps;
  datepicker: DatePickerProps;
  email: InputProps;
};

interface initialValuesMap {
  [key: string]: FieldType[];
}

export const formComponentsMap: FormComponentMapEntry<FormComponentPropsMap> = {
  dropdown: Select,
  radio: RadioGroup,
  number: NumberInput,
  single_checkbox: Checkbox,
  multiple_checkboxes: Multiselect,
  multi_line_text: Textarea,
  single_line_text: Input,
  file: FileUploader,
  phone: Input,
  datepicker: DatePicker,
  email: Input,
};

export const initialValuesMap: initialValuesMap = {
  string: [
    FieldType.SingleLineText,
    FieldType.MultiLineText,
    FieldType.Radio,
    FieldType.Datepicker,
    FieldType.Dropdown,
    FieldType.Email,
    FieldType.Phone,
    FieldType.File,
  ],
  boolean: [FieldType.SingleCheckbox],
  number: [FieldType.Number],
  array: [FieldType.MultipleCheckboxes],
  date: [FieldType.Datepicker],
};

export interface SubmitFormProps extends CanHaveHubspotRegion, HasHubspotFormId {
  form: HubSpotFormEntity;
  values: Record<string, any>;
}

/**
 * Converts the form info to what we send to HubSpot during form submission
 */
export function convertValuesToHubSpotSubmitObject({
  form,
  hubspotFormId,
  values,
}: Omit<SubmitFormProps, 'hubspotRegion'>): HubSpotSubmitObject {
  let legalConsentOptions;
  if (form.legalConsentOptions.type !== HubSpotLegalConsentOptionsType.None) {
    legalConsentOptions = {
      consent: {
        consentToProcess:
          form.legalConsentOptions.type === HubSpotLegalConsentOptionsType.ExplicitConsentToProcess
            ? values.consentToProcess
            : true,
        text: form.legalConsentOptions.consentToProcessText ?? '',
        communications: form.legalConsentOptions.communicationsCheckboxes?.map(checkbox => ({
          value: values[`communication__${checkbox.subscriptionTypeId}`],
          subscriptionTypeId: checkbox.subscriptionTypeId,
          text: checkbox.label,
        })),
      },
    };
  }

  return {
    // Required fields
    fields: formatFormikToHubSpotFields(
      Object.fromEntries(
        Object.entries(values).filter(
          // We ignore the communication checkboxes, the consentToProcess checkbox and the recaptcha response
          // because those are handled separately
          ([key, _value]) =>
            !key.startsWith('communication__') && key !== 'consentToProcess' && key !== 'gRecaptchaResponse'
        )
      ),
      form
    ),
    hubspotFormId,
    context: getHubSpotSubmitContext(),
    // Legal consent fields
    legalConsentOptions: legalConsentOptions,
    gRecaptchaResponse: values.gRecaptchaResponse,
  };
}

export async function submitForm({
  form,
  hubspotFormId,
  hubspotRegion = DEFAULT_HUBSPOT_REGION,
  values,
}: SubmitFormProps) {
  return cmsWebClient.post(`hubspot-proxy/submitForm?region=${hubspotRegion}`, {
    json: convertValuesToHubSpotSubmitObject({ values, hubspotFormId, form }),
  });
}

export interface GetFormByIdProps extends CanHaveHubspotRegion, HasHubspotFormId {}

export async function getFormById({ hubspotFormId, hubspotRegion = DEFAULT_HUBSPOT_REGION }: GetFormByIdProps) {
  return cmsWebClient.get(`hubspot-proxy/getForm/${hubspotFormId}?region=${hubspotRegion}`).json<HubSpotFormEntity>();
}

export interface UploadFileProps extends CanHaveHubspotRegion {
  /**
   * The file to upload.
   */
  file: UploadFile;
}

export async function uploadFile({ file, hubspotRegion = DEFAULT_HUBSPOT_REGION }: UploadFileProps) {
  const formData = new FormData();
  formData.append('file', file.file);
  return cmsWebClient
    .post(`hubspot-proxy/uploadFile?region=${hubspotRegion}`, { body: formData })
    .json<{ objects: [{ url: string; id: number }] }>();
}

export interface DeleteFileProps extends CanHaveHubspotRegion {
  /**
   * The file to delete.
   */
  file: UploadFile;
}

export async function deleteFile({ file, hubspotRegion = DEFAULT_HUBSPOT_REGION }: DeleteFileProps) {
  if (!file.responseFile?.id) return Promise.reject('No file id found');
  return cmsWebClient.delete(`hubspot-proxy/deleteFile/${file.responseFile.id}?region=${hubspotRegion}`);
}

export function formatFormikToHubSpotFields(formikValues: object, form: HubSpotFormEntity) {
  /**
   * Some fields are getting prefixed with `TICKET.` in order to be accepted by HubSpot.
   * There's a fixed set of field ids that are affected by this.
   * This is probably done because those fields open a ticket in HubSpot.
   * We used to do add the prefix here, but it seems like HubSpot does that for us.
   */
  // const ticketFields = ['subject', 'content', 'hs_file_upload'];

  const transformValue = (value: any) => {
    if (value instanceof Date) {
      // We need to transform date objects into `YYYY-MM-DD`.
      return format(value, 'yyyy-MM-dd');
    }
    return value;
  };

  return Object.keys(formikValues).reduce((fields: any[], name) => {
    let fieldInForm: HubSpotField | undefined = undefined;
    for (const group of form.fieldGroups) {
      if (!group.fields) {
        continue;
      }
      const newFieldInForm = group.fields.find(field => field.name === name);
      if (newFieldInForm) {
        fieldInForm = newFieldInForm;
        break;
      }
    }

    if (Array.isArray(formikValues[name])) {
      formikValues[name].forEach(value => {
        fields.push({ name, value: transformValue(value) });
      });
    } else {
      fields.push({
        objectTypeId: fieldInForm ? fieldInForm?.objectTypeId : null,
        name,
        value: transformValue(formikValues[name]),
      });
    }

    return fields;
  }, []);
}

export const getInitialValues = (form: HubSpotFormEntity) => {
  const initialValues = {};

  // Regular fields
  (form.fieldGroups ?? []).forEach(group => {
    if (!group.fields) {
      return;
    }

    group.fields.forEach(field => {
      initialValues[field.name] =
        castValueToType(field.fieldType, field.defaultValue ?? field.defaultValues) ??
        createInitialValue(field.fieldType);

      field.dependentFields?.forEach(entry => {
        initialValues[entry.dependentField.name] =
          castValueToType(
            entry.dependentField.fieldType,
            entry.dependentCondition.value ?? entry.dependentCondition.value
          ) ?? createInitialValue(entry.dependentField.fieldType);
      });
    });
  });

  // Communication consent fields
  (form.legalConsentOptions?.communicationsCheckboxes ?? []).forEach(legalField => {
    initialValues['communication__' + legalField.subscriptionTypeId] = false;
  });

  // Consent to process fields
  if (form.legalConsentOptions?.consentToProcessCheckboxLabel) {
    initialValues['consentToProcess'] = false;
  }

  return initialValues;
};

export const createInitialValue = (fieldType: FieldType) => {
  if (initialValuesMap.string.includes(fieldType)) {
    return '';
  }
  if (initialValuesMap.boolean.includes(fieldType)) {
    return false;
  }
  if (initialValuesMap.number.includes(fieldType)) {
    return 0;
  }
  if (initialValuesMap.array.includes(fieldType)) {
    return [];
  }
  if (initialValuesMap.date.includes(fieldType)) {
    return null;
  }
  return '';
};

export const castValueToType = (fieldType: FieldType, value?: string | string[]) => {
  if (!value) return;
  if (initialValuesMap.string.includes(fieldType)) {
    if (typeof value === 'object') {
      // Initial hubspot values of dropdowns are arrays, but are stored as strings in the formik values
      return value[0];
    }
    return value;
  }
  if (initialValuesMap.boolean.includes(fieldType)) {
    return value === 'true';
  }
  if (initialValuesMap.number.includes(fieldType)) {
    return Number(value);
  }
  if (initialValuesMap.array.includes(fieldType)) {
    return value;
  }
  return value;
};

export const getInputTypeFromFieldType = (fieldType: FieldType) => {
  if (fieldType === FieldType.SingleLineText) return 'text';
  if (fieldType === FieldType.SingleCheckbox) return 'checkbox';
  return fieldType;
};

const fieldToYupSchemaMap: Record<FieldType, Yup.AnySchema> = {
  [FieldType.SingleLineText]: Yup.string(),
  [FieldType.MultiLineText]: Yup.string(),
  [FieldType.SingleCheckbox]: Yup.boolean(),
  [FieldType.Number]: Yup.number(),
  [FieldType.MultipleCheckboxes]: Yup.array(),
  [FieldType.Radio]: Yup.string(),
  [FieldType.Dropdown]: Yup.string(),
  [FieldType.Datepicker]: Yup.date(),
  [FieldType.Email]: Yup.string().email(),
  [FieldType.Phone]: Yup.string(),
  [FieldType.File]: Yup.string(),
};

function isStringSchema(schema: Yup.AnySchema): schema is Yup.StringSchema {
  return schema instanceof Yup.StringSchema;
}

export class FormValidationManager {
  private hubspotForm: HubSpotFormEntity;

  constructor(hubspotForm: HubSpotFormEntity) {
    this.hubspotForm = hubspotForm;
  }

  public createYupSchema() {
    const schema = {};
    // regular fields
    this.hubspotForm.fieldGroups.forEach(group => {
      if (!group.fields) {
        return;
      }
      group.fields.forEach(field => {
        schema[field.name] = this.createYupFieldSchema(field);

        field.dependentFields?.forEach(entry => {
          schema[entry.dependentField.name] = this.createYupFieldSchema(
            entry.dependentField,
            entry.dependentCondition,
            field.name
          );
        });
      });
    });
    // Communication consent fields
    (this.hubspotForm.legalConsentOptions?.communicationsCheckboxes ?? []).forEach(legalField => {
      const fieldName = 'communication__' + legalField.subscriptionTypeId;
      schema[fieldName] = this.createYupFieldSchema({
        ...legalField,
        objectTypeId: '',
        name: fieldName,
        hidden: false,
        fieldType: FieldType.SingleCheckbox,
      });
    });
    // Consent to process fields
    if (this.hubspotForm.legalConsentOptions?.consentToProcessCheckboxLabel) {
      schema['consentToProcess'] = this.createYupFieldSchema({
        label: this.hubspotForm.legalConsentOptions.consentToProcessCheckboxLabel,
        required: true,
        objectTypeId: '',
        name: 'consentToProcess',
        hidden: false,
        fieldType: FieldType.SingleCheckbox,
      });
    }

    return Yup.object(schema);
  }

  private createYupFieldSchema(
    field: HubSpotField,
    // Add the dependent condition if this field is a dependent field
    dependentCondition?: HubSpotDependentCondition,
    // We need the parent name to know which field to validate this dependent field against
    parentName?: string
  ) {
    let yupSchema = fieldToYupSchemaMap[field.fieldType];
    if (!yupSchema) {
      throw new Error(`Field type ${field.fieldType} not supported`);
    }

    if (field.required) {
      if (initialValuesMap.array.includes(field.fieldType)) {
        yupSchema = (yupSchema as OptionalArraySchema<Yup.AnySchema, AnyObject, any[] | undefined>).min(1);
      } else {
        yupSchema = yupSchema.required();
      }
    }

    if (isStringSchema(yupSchema)) {
      const min = field.validation?.minAllowedDigits;
      if (min) yupSchema = (yupSchema as Yup.StringSchema).min(min);
      const max = field.validation?.maxAllowedDigits;
      if (max) yupSchema = (yupSchema as Yup.StringSchema).max(max);
    }

    if (dependentCondition && parentName) {
      const validateFn = HubSpotOperatorToValidationFnMap[dependentCondition.operator];
      yupSchema = Yup.mixed().when(parentName, {
        is: parentValue => validateFn(parentValue, dependentCondition.value ?? dependentCondition.values),
        then: yupSchema,
        otherwise: Yup.mixed().nullable(),
      });
    }

    return yupSchema;
  }
}

export const hubSpotOptionsToSelectOptions = (options: HubSpotOption[]) => {
  return options
    .map(option => ({
      label: option.label,
      value: option.value,
    }))
    .filter(option => option.value !== undefined);
};

export const hubspotFieldToComponentProps = (
  hubSpotField: HubSpotField,
  themeColor: FormElementThemeColor,
  hubspotRegion: HubspotRegion = DEFAULT_HUBSPOT_REGION
) => {
  if (
    hubSpotField.fieldType === FieldType.SingleLineText ||
    hubSpotField.fieldType === FieldType.Phone ||
    hubSpotField.fieldType === FieldType.Email
  ) {
    const transformedProps: InputProps = {
      name: hubSpotField.name,
      type: getInputTypeFromFieldType(hubSpotField.fieldType),
      label: hubSpotField.label,
      placeholder: hubSpotField.placeholder,
      required: hubSpotField.required,
      themeColor: themeColor,
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.MultiLineText) {
    const transformedProps: TextareaProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      placeholder: hubSpotField.placeholder,
      required: hubSpotField.required,
      themeColor: themeColor,
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.SingleCheckbox) {
    const transformedProps: CheckboxProps = {
      name: hubSpotField.name,
      children: hubSpotField.label,
      required: hubSpotField.required,
      themeColor: themeColor,
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.Number) {
    const transformedProps: NumberInputProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      placeholder: hubSpotField.placeholder,
      required: hubSpotField.required,
      themeColor: themeColor,
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.MultipleCheckboxes) {
    const transformedProps: MultiselectProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      required: hubSpotField.required,
      themeColor: themeColor,
      options: hubSpotOptionsToSelectOptions(hubSpotField.options || []),
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.Radio) {
    const transformedProps: RadioGroupProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      required: hubSpotField.required,
      themeColor: themeColor,
      options: hubSpotOptionsToSelectOptions(hubSpotField.options || []),
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.Dropdown) {
    const transformedProps: SelectProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      required: hubSpotField.required,
      themeColor: themeColor,
      options: hubSpotOptionsToSelectOptions(hubSpotField.options || []),
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.Datepicker) {
    const transformedProps: DatePickerProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      required: hubSpotField.required,
      themeColor: themeColor,
    };

    return transformedProps;
  }

  if (hubSpotField.fieldType === FieldType.File) {
    const transformedProps: FileUploaderProps = {
      name: hubSpotField.name,
      label: hubSpotField.label,
      required: hubSpotField.required,
      themeColor: themeColor,
      onDrop: async files => {
        if (!files || files.length === 0) {
          return null;
        }
        const res = await uploadFile({ file: files[0], hubspotRegion });
        const responseFile = res.objects[0];
        return { ...files[0], responseFile };
      },
      onRemove: async (file: UploadFile) => {
        deleteFile({ file, hubspotRegion });
      },
    };

    return transformedProps;
  }
};

export function getLegalCheckboxText(form: HubSpotFormEntity): string | null {
  return form.legalConsentOptions?.communicationsCheckboxes?.[0].label ?? form.legalConsentOptions?.privacyText ?? null;
}

/**
 * This function returns the label for a field in a HubSpot form.
 *
 * @param hubspotFormData The form data from HubSpot
 * @param fieldName The field name to get the label for
 * @returns the label for the field
 */
export const getHubspotFieldLabel = (hubspotFormData: HubSpotFormEntity, fieldName: string) => {
  for (const group of hubspotFormData?.fieldGroups ?? []) {
    for (const field of group?.fields ?? []) {
      if (field.name === fieldName) {
        return field.label;
      }
    }
  }
};

export function getHubSpotSubmitContext(): HubSpotSubmitContext {
  const hutkCookie = JSCookie.get('hubspotutk') ?? '';

  const context: HubSpotSubmitContext = {
    pageUri: window.location.href,
    pageName: document.title,
  };

  if (hutkCookie) {
    context.hutk = JSCookie.get('hubspotutk') ?? '';
  }

  return context;
}
